import React from 'react';
import { Box, Typography } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { DashboardWidget, WidgetDataResult } from '../../../redux/wallboardApi';
import { useDashboardContext, WIDGET_COLORS } from '../pages/DashboardViewPage';

/**
 * Pie Chart Widget
 * 
 * Displays data as a pie or donut chart.
 * Supports dynamic font sizing and themed colors.
 */
interface PieChartWidgetProps {
  widget: DashboardWidget;
  data?: WidgetDataResult;
  isDonut?: boolean;
  colorIndex?: number;
}

export const PieChartWidget: React.FC<PieChartWidgetProps> = ({
  widget,
  data,
  isDonut = false,
  colorIndex = 0,
}) => {
  const displayOptions = widget.config?.displayOptions || {};
  const { fontSize, onDrillDown } = useDashboardContext();
  
  // Get color theme based on index
  const colorTheme = WIDGET_COLORS[colorIndex % WIDGET_COLORS.length];
  // Use valueColor for text on white background, header for chart colors
  const textColor = colorTheme.valueColor || colorTheme.header;
  const chartColor = colorTheme.header;

  // Check if data is single value (no groupBy configured)
  const isSingleValue = data?.data && 
    typeof data.data === 'object' && 
    !Array.isArray(data.data) && 
    'value' in data.data && 
    !('labels' in data.data);

  // Parse data
  const chartData = React.useMemo(() => {
    if (!data?.data) return [];

    // Handle grouped data format
    if (data.data.labels && data.data.values) {
      return data.data.labels.map((label: string, index: number) => ({
        id: index,
        value: data.data.values[index] || 0,
        label: label || 'Unknown',
      }));
    }

    // Handle array format
    if (Array.isArray(data.data)) {
      return data.data.map((item: any, index: number) => ({
        id: index,
        value: item.value || item.count || 0,
        label: item.label || item.name || `Item ${index + 1}`,
      }));
    }

    return [];
  }, [data]);

  // Single value - need groupBy for pie charts
  if (isSingleValue) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          p: 2,
        }}
      >
        <Typography 
          sx={{ 
            color: textColor, 
            fontSize: `${1.5 * fontSize}rem`,
            fontWeight: 'bold',
            mb: 1,
          }}
        >
          {(data?.data?.value ?? 0).toLocaleString()}
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: `${0.75 * fontSize}rem` }}>
          Configure &quot;Group By&quot; in widget settings for pie chart
        </Typography>
      </Box>
    );
  }

  // No data
  if (chartData.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: `${0.875 * fontSize}rem` }}>No data available</Typography>
      </Box>
    );
  }

  // Color palette - use chart color as primary, then generate variations
  const colors = displayOptions.colorScheme === 'monochrome'
    ? [chartColor, `${chartColor}cc`, `${chartColor}99`, `${chartColor}66`, `${chartColor}33`]
    : [chartColor, '#388e3c', '#f57c00', '#d32f2f', '#7b1fa2', '#0288d1', '#689f38', '#ffa000'];

  return (
    <Box sx={{ width: '100%', height: '100%', minHeight: 200 }}>
      <PieChart
        series={[
          {
            data: chartData,
            innerRadius: isDonut ? 40 : 0,
            outerRadius: 80,
            paddingAngle: 2,
            cornerRadius: 4,
          },
        ]}
        colors={colors}
        margin={{ top: 10, bottom: 10, left: 10, right: 10 }}
        onItemClick={(_event, params) => {
          const item = chartData[params.dataIndex];
          if (item) onDrillDown(widget.id, widget.name, item.label);
        }}
      />
    </Box>
  );
};

export default PieChartWidget;
